/**
 * 149-cw-r5 (PROMPT 16-PRESENCE-SESSION-R1 H): Session History tab.
 * Riwayat login/logout/IDLE_TIMEOUT/KICKED — filter + pagination.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, X, RefreshCcw, Ban, AlertTriangle, Trash2 } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { DIVISION_COLOR } from "@/lib/constants";
import { fmtRelativeLastSeen } from "@/lib/presence";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";

const EVENT_META = {
  login: { label: "LOGIN", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  logout: { label: "LOGOUT", cls: "bg-slate-50 text-slate-700 border-slate-300" },
  IDLE_TIMEOUT: { label: "IDLE TIMEOUT", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  KICKED: { label: "KICKED", cls: "bg-rose-50 text-rose-700 border-rose-300" },
  SESSION_REVOKED: { label: "REVOKED", cls: "bg-rose-50 text-rose-700 border-rose-300" },
  PASSWORD_CHANGED: { label: "PASS CHANGED", cls: "bg-indigo-50 text-indigo-700 border-indigo-300" },
  PASSWORD_RESET: { label: "PASS RESET", cls: "bg-indigo-50 text-indigo-700 border-indigo-300" },
  ACCOUNT_DISABLED: { label: "DISABLED", cls: "bg-slate-100 text-slate-700 border-slate-400" },
  LOGIN_FAILED: { label: "LOGIN FAILED", cls: "bg-rose-50 text-rose-700 border-rose-300" },
};

const ALL_DIVS = ["BOD", "FAT", "Procurement Asset Logistic", "Project", "HRGA"];
const ALL_EVENTS = ["", "login", "logout", "IDLE_TIMEOUT", "KICKED", "SESSION_REVOKED", "PASSWORD_CHANGED", "PASSWORD_RESET", "ACCOUNT_DISABLED"];

export default function SessionHistoryTab() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  // 2026-09-25 (user request): Revoke per-sesi — gate sama dgn force-logout
  // (BOD Lv1 / user_management.update).
  const canRevoke = (user?.division === "BOD" && Number(user?.level) === 1) || canPerm(user, "user_management", "update");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [division, setDivision] = useState("");
  const [action, setAction] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busyId, setBusyId] = useState("");
  const [showDup, setShowDup] = useState(false);

  // NOTE: Filter values (q/division/action/dates) intentionally excluded dari deps.
  // Filter di-apply MANUAL via tombol `applyFilters`/`resetFilters` (bukan on-change),
  // supaya user selesai isi form dulu baru fetch. Auto-reload hanya on page change.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (division) params.set("division", division);
      if (action) params.set("action", action);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("page", String(page));
      params.set("limit", "50");
      const { data } = await api.get(`/admin/login-events?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat riwayat sesi.");
      setItems([]); setTotal(0); setPages(0);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
  useEffect(() => { load(); }, [load]);
  const applyFilters = () => { setPage(1); load(); };
  const resetFilters = () => {
    setQ(""); setDivision(""); setAction(""); setDateFrom(""); setDateTo(""); setPage(1);
    setTimeout(load, 0);
  };

  const hasFilters = q || division || action || dateFrom || dateTo;

  // 2026-09-25 (user request): Revoke satu sesi aktif (bukan semua sesi user).
  const revoke = async (r) => {
    const fp = r.session_fingerprint || "";
    const selfWarn = r.user_id === user?.id
      ? "\n\nPERINGATAN: ini sesi Anda SENDIRI — Anda akan langsung logout setelah revoke."
      : "";
    if (!window.confirm(
      `Revoke sesi ${fp} milik ${r.user_name || r.user_email || "user ini"}?\n\n` +
      `Hanya sesi ini yang diakhiri. Event akan tercatat sebagai REVOKED di audit log & riwayat sesi.${selfWarn}`
    )) return;
    setBusyId(r.session_id);
    try {
      await api.post(`/admin/users/${r.user_id}/sessions/${r.session_id}/revoke`);
      toast.success(`Sesi ${fp} berhasil di-revoke.`);
      await load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal revoke sesi.");
    } finally { setBusyId(""); }
  };

  return (
    <section data-testid="session-history-tab" className="bg-white border border-slate-200">
      {/* Filter bar */}
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 bg-slate-50">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" data-testid="sh-q" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            placeholder="Cari email / IP…"
            className="pl-7 pr-7 py-1.5 border border-slate-300 text-xs w-52 focus:outline-none focus:border-[#0B1D3A]"
          />
          {q && <button onClick={() => setQ("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-rose-600"><X size={12} /></button>}
        </div>
        <select data-testid="sh-division" value={division} onChange={(e) => setDivision(e.target.value)} className="border border-slate-300 py-1.5 px-2 text-xs bg-white">
          <option value="">Semua Divisi</option>
          {ALL_DIVS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select data-testid="sh-action" value={action} onChange={(e) => setAction(e.target.value)} className="border border-slate-300 py-1.5 px-2 text-xs bg-white">
          {ALL_EVENTS.map((a) => <option key={a || "all"} value={a}>{a ? (EVENT_META[a]?.label || a) : "Semua Event"}</option>)}
        </select>
        <input type="date" data-testid="sh-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border border-slate-300 py-1.5 px-2 text-xs bg-white" placeholder="Dari" />
        <input type="date" data-testid="sh-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border border-slate-300 py-1.5 px-2 text-xs bg-white" placeholder="Sampai" />
        <button type="button" data-testid="sh-apply" onClick={applyFilters} className="text-xs uppercase tracking-wider font-semibold px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#082033] text-white">
          Filter
        </button>
        {hasFilters && (
          <button type="button" data-testid="sh-reset" onClick={resetFilters} className="text-[10px] text-slate-500 hover:text-[#FF5A1F] underline">× Reset filter</button>
        )}
        {canRevoke && (
          <button type="button" data-testid="sh-dup-check" onClick={() => setShowDup(true)}
            title="Laporan duplikat historis event LOGOUT + dry-run cleanup"
            className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1.5 border border-amber-300 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1">
            <AlertTriangle size={11} /> Cek Duplikat
          </button>
        )}
        <button type="button" data-testid="sh-reload" onClick={load} className="ml-auto text-slate-500 hover:text-[#FF5A1F] p-1" title="Refresh">
          <RefreshCcw size={13} />
        </button>
        <div className="text-[10px] text-slate-500 tabular-nums" data-testid="sh-count">
          {loading ? "…" : `${total} event · Page ${page}/${Math.max(1, pages)}`}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="sh-table">
          <thead className="bg-slate-100 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-3 py-2 overline text-slate-600 whitespace-nowrap">Waktu</th>
              <th className="px-3 py-2 overline text-slate-600 whitespace-nowrap">User</th>
              <th className="px-3 py-2 overline text-slate-600 whitespace-nowrap">Divisi · Lv</th>
              <th className="px-3 py-2 overline text-slate-600 whitespace-nowrap">Event</th>
              <th className="px-3 py-2 overline text-slate-600 whitespace-nowrap">IP</th>
              <th className="px-3 py-2 overline text-slate-600 whitespace-nowrap">Session</th>
              <th className="px-3 py-2 overline text-slate-600 whitespace-nowrap">Idle (s)</th>
              <th className="px-3 py-2 overline text-slate-600 whitespace-nowrap">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400 italic" data-testid="sh-loading">Memuat…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400 italic" data-testid="sh-empty">Tidak ada event sesuai filter.</td></tr>
            )}
            {!loading && items.map((r, i) => {
              const created = r.created_at || r.at || "";
              const meta = EVENT_META[r.action] || { label: r.action, cls: "bg-slate-50 text-slate-700 border-slate-300" };
              return (
                <tr key={`${r.id || i}-${created}`} data-testid={`sh-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{fmtRelativeLastSeen(created)}</td>
                  <td className="px-3 py-1.5 min-w-[180px]">
                    <div className="font-semibold text-[#0B1D3A] truncate max-w-[220px]" title={r.user_name}>{r.user_name || "—"}</div>
                    <div className="text-[10px] text-slate-500 truncate max-w-[220px]" title={r.user_email}>{r.user_email || ""}</div>
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {r.user_division ? (
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase border ${DIVISION_COLOR[r.user_division] || "bg-slate-100 border-slate-300 text-slate-700"}`}>
                        {r.user_division}
                      </span>
                    ) : "—"}
                    {r.user_level ? <span className="ml-1 text-[10px] text-slate-500 tabular-nums">Lv{r.user_level}</span> : null}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase border ${meta.cls}`}>{meta.label}</span>
                  </td>
                  <td className="px-3 py-1.5 tabular-nums text-slate-600">{r.ip_masked || "—"}</td>
                  <td className="px-3 py-1.5 tabular-nums text-slate-500 text-[10px]">{r.session_fingerprint || "—"}</td>
                  <td className="px-3 py-1.5 tabular-nums text-slate-600 text-right">{r.idle_seconds ? `${Math.round(r.idle_seconds/60)}m` : "—"}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {/* 2026-09-25 (user request): tombol Revoke hanya untuk sesi
                        yang MASIH AKTIF (is_active_session). Sesi berakhir → tidak
                        ada tombol. KICK semua sesi tetap di daftar Users. */}
                    {r.is_active_session && r.session_id ? (
                      canRevoke ? (
                        <button type="button"
                          onClick={() => revoke(r)}
                          disabled={busyId === r.session_id}
                          data-testid={`sh-revoke-${i}`}
                          title={`Revoke sesi ${r.session_fingerprint} — hanya sesi ini yang diakhiri`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[9px] uppercase font-bold disabled:opacity-40">
                          <Ban size={10} /> {busyId === r.session_id ? "…" : "Revoke"}
                        </button>
                      ) : (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-300" data-testid={`sh-active-${i}`}>Aktif</span>
                      )
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="px-4 py-2 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button type="button" data-testid="sh-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 border border-slate-300 text-[10px] uppercase font-semibold disabled:opacity-40 hover:bg-white">‹ Prev</button>
          <span className="text-[10px] tabular-nums text-slate-600">{page} / {pages}</span>
          <button type="button" data-testid="sh-next" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}
            className="px-3 py-1 border border-slate-300 text-[10px] uppercase font-semibold disabled:opacity-40 hover:bg-white">Next ›</button>
        </div>
      )}
      {showDup && <DuplicatesDialog onClose={() => setShowDup(false)} onCleaned={load} />}
    </section>
  );
}

/**
 * 2026-09-25 (fix logout duplikat): Dialog laporan duplikat historis LOGOUT.
 * Default READ-ONLY; dry-run cleanup menunjukkan jumlah yang akan dihapus;
 * penghapusan nyata butuh konfirmasi + perm data_integrity.repair di backend.
 */
function DuplicatesDialog({ onClose, onCleaned }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/login-events/duplicates");
      setReport(data);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat laporan duplikat.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadReport(); }, [loadReport]);

  const runCleanup = async (dryRun) => {
    if (!dryRun && !window.confirm(
      `Hapus permanen ${report?.duplicate_extras ?? 0} event LOGOUT duplikat?\n\n` +
      `Event paling awal di setiap grup DIPERTAHANKAN. Aksi ini tercatat di audit log.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login-events/duplicates/cleanup", { dry_run: dryRun });
      if (dryRun) {
        toast.info(`Dry-run: ${data.would_delete} event akan dihapus dari ${data.groups} grup duplikat.`);
      } else {
        toast.success(`${data.deleted} event duplikat dihapus.`);
        await loadReport();
        onCleaned?.();
      }
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Cleanup gagal.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="sh-dup-dialog">
      <div className="bg-white border border-slate-200 shadow-2xl w-full max-w-2xl mt-10">
        <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div>
            <p className="overline text-[9px] text-slate-500">Riwayat Login &amp; Sesi</p>
            <h2 className="font-display text-lg text-[#0B1D3A]">Laporan Duplikat Event LOGOUT</h2>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Grup = sesi yang sama dengan event berjarak ≤ 2 detik. Histori lama TIDAK dihapus otomatis.
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="sh-dup-close" aria-label="Tutup"
            className="p-1.5 border border-slate-300 hover:bg-slate-100"><X size={13} /></button>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-xs text-slate-400 italic">Menganalisis histori…</div>
          ) : !report ? (
            <div className="text-center py-8 text-xs text-slate-400 italic">Laporan tidak tersedia.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-slate-200 px-3 py-2">
                  <div className="overline text-[9px] text-slate-500">Total Event LOGOUT</div>
                  <div className="font-display text-xl tabular-nums" data-testid="sh-dup-total">{report.total_events}</div>
                </div>
                <div className="border border-slate-200 px-3 py-2">
                  <div className="overline text-[9px] text-slate-500">Grup Duplikat</div>
                  <div className="font-display text-xl tabular-nums" data-testid="sh-dup-groups">{report.duplicate_groups}</div>
                </div>
                <div className="border border-slate-200 px-3 py-2">
                  <div className="overline text-[9px] text-slate-500">Dokumen Berlebih</div>
                  <div className={`font-display text-xl tabular-nums ${report.duplicate_extras > 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="sh-dup-extras">{report.duplicate_extras}</div>
                </div>
              </div>

              {report.duplicate_groups === 0 ? (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="sh-dup-clean">
                  Tidak ada duplikat — histori LOGOUT bersih.
                </div>
              ) : (
                <table className="w-full text-xs border border-slate-200">
                  <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="px-2 py-1.5 text-left">User</th>
                      <th className="px-2 py-1.5 text-left">Session</th>
                      <th className="px-2 py-1.5 text-right">Jumlah Event</th>
                      <th className="px-2 py-1.5 text-left">Rentang</th>
                    </tr>
                  </thead>
                  <tbody data-testid="sh-dup-table">
                    {(report.groups || []).map((g, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`sh-dup-row-${i}`}>
                        <td className="px-2 py-1.5 font-semibold text-[#0B1D3A]">{g.user_name}</td>
                        <td className="px-2 py-1.5 tabular-nums text-slate-500">{g.session_fingerprint}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-rose-600">{g.count}×</td>
                        <td className="px-2 py-1.5 text-slate-500 tabular-nums">{fmtRelativeLastSeen(g.first_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button type="button" onClick={() => runCleanup(true)} disabled={busy || !report || report.duplicate_extras === 0}
                  data-testid="sh-dup-dryrun"
                  className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold disabled:opacity-40">
                  Dry-Run Cleanup
                </button>
                <button type="button" onClick={() => runCleanup(false)} disabled={busy || !report || report.duplicate_extras === 0}
                  data-testid="sh-dup-delete"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-40">
                  <Trash2 size={11} /> {busy ? "Memproses…" : "Hapus Duplikat"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
